import { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Select,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertCircle, Clock } from 'react-feather';
import type { GroupWithMembers } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';
import { sessionsApi } from '../../../services/api/sessions';
import type { TimeSlotResponse } from '../../../types';
import { getTimezoneAbbreviation } from '../../../utils/timezone';

interface AddTimeSlotModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  group: GroupWithMembers | null;
  timeSlots: TimeSlotResponse[];
  onAssigned?: () => void;
}

// Format time slot for display
const formatTimeSlotOption = (slot: TimeSlotResponse): string => {
  const dayMap: Record<string, string> = {
    monday: 'Mondays',
    tuesday: 'Tuesdays',
    wednesday: 'Wednesdays',
    thursday: 'Thursdays',
    friday: 'Fridays',
    saturday: 'Saturdays',
    sunday: 'Sundays',
  };
  const day = dayMap[slot.day_of_week.toLowerCase()] || slot.day_of_week;

  // Format time
  const timeParts = slot.time.split(':');
  let hours = parseInt(timeParts[0], 10);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  const tz = getTimezoneAbbreviation(slot.timezone);

  return `${day} ${formattedTime} ${tz}`;
};

export function AddTimeSlotModal({
  opened,
  onClose,
  cohortId,
  group,
  timeSlots,
  onAssigned,
}: AddTimeSlotModalProps) {
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Reset selection when modal opens/closes
  const handleClose = () => {
    setSelectedSlotId(null);
    onClose();
  };

  // Handle assign time slot
  const handleAssign = async () => {
    if (!group || !selectedSlotId) return;

    const selectedSlot = timeSlots.find((s) => s.id === selectedSlotId);
    if (!selectedSlot) return;

    setAssigning(true);
    try {
      // Update group with the time slot identifier
      await groupsApi.update(cohortId, group.id, {
        timeSlotIdentifier: selectedSlot.identifier,
      });

      // Generate sessions for the group (will be created with the new time slot)
      const result = await sessionsApi.generateForCohort(cohortId);

      // MED-1082: the generate endpoint swallows per-group failures into an
      // errors[] array and still returns 200, so a real failure used to surface
      // as a green "Sessions have been generated" toast over an empty Sessions
      // tab. Branch on the result so the message reflects what actually happened.
      const assignedTo = `${group.name} has been assigned to ${formatTimeSlotOption(selectedSlot)}.`;

      if (result.errors.length > 0) {
        notifications.show({
          title: 'Time Slot Assigned — Session Generation Failed',
          message: `${assignedTo} Sessions could NOT be generated: ${result.errors
            .map((e) => e.error)
            .join('; ')}. Check the cohort's start date and session schedule, then try again.`,
          color: 'red',
          autoClose: false,
        });
      } else if (result.totalSessions > 0) {
        notifications.show({
          title: 'Time Slot Assigned',
          message: `${assignedTo} ${result.totalSessions} session${
            result.totalSessions === 1 ? '' : 's'
          } generated.`,
          color: 'green',
        });
      } else {
        // No errors but nothing created — typically the group already had
        // sessions (idempotent re-run). Report the assignment without falsely
        // claiming new sessions were generated.
        notifications.show({
          title: 'Time Slot Assigned',
          message: `${assignedTo} No new sessions were generated (they may already exist).`,
          color: 'green',
        });
      }

      onAssigned?.();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to assign time slot',
        color: 'red',
      });
    } finally {
      setAssigning(false);
    }
  };

  // Filter out time slots that are already assigned to other groups
  // For now, show all available time slots
  const timeSlotOptions = timeSlots.map((slot) => ({
    value: slot.id,
    label: formatTimeSlotOption(slot),
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add Time Slot to Group"
      size="md"
    >
      <Stack gap="md">
        {group && (
          <Text size="sm">
            Select a time slot for <strong>{group.name}</strong>. Sessions will be automatically
            generated for this group based on the cohort's session schedule.
          </Text>
        )}

        {timeSlotOptions.length === 0 ? (
          <Alert
            icon={<AlertCircle size={16} />}
            title="No Time Slots Available"
            color="yellow"
          >
            This cohort has no time slots configured. Please add time slots to the cohort first.
          </Alert>
        ) : (
          <Select
            label="Select Time Slot"
            placeholder="Choose a time slot"
            leftSection={<Clock size={14} />}
            data={timeSlotOptions}
            value={selectedSlotId}
            onChange={setSelectedSlotId}
            searchable
          />
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            loading={assigning}
            disabled={!selectedSlotId || timeSlotOptions.length === 0}
          >
            Assign Time Slot
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddTimeSlotModal;
